import { useCallback, useEffect, useState } from 'react';
import { Link2, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Attaches an event to a hackathon.
 *
 * Registration happens on the event — that is where the form, the fee and the
 * questions are — while teams, problem statements, mentors and judges belong to
 * the hackathon and find people through this link. Without it, everyone who
 * registers is invisible to all of that.
 */

interface Hackathon { _id: string; title: string }

interface Props {
  value: string;
  onChange: (hackathonId: string) => void;
  /** Present once the event exists, so its earlier registrations can be caught up. */
  eventId?: string;
}

export default function HackathonLinkField({ value, onChange, eventId }: Props) {
  const { success, error } = useToast();
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/hackathons');
      const list = Array.isArray(res.data) ? res.data : (res.data.hackathons || []);
      setHackathons(list.map((h: any) => ({ _id: h._id, title: h.title })));
    } catch {
      // A missing list only means the picker is empty; the event still saves.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const linkExisting = async () => {
    if (!eventId) return;
    if (!window.confirm(
      'Bring the people already registered for this event into the hackathon?\n\n' +
      'They will become available for team formation. Nobody is emailed and nothing else changes.'
    )) return;

    setLinking(true);
    try {
      const res = await api.post(`/events/${eventId}/link-registrations`);
      success(res.data.message);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not link the existing registrations');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="border-t pt-4">
      <label htmlFor="hackathonLink" className="mb-1 block text-sm font-medium text-gray-700">
        Part of which hackathon?
      </label>
      <select
        id="hackathonLink"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">Not part of a hackathon</option>
        {hackathons.map((h) => <option key={h._id} value={h._id}>{h.title}</option>)}
      </select>
      <p className="mt-1 text-sm text-gray-500">
        People register through this event, and linking it lets team formation, problem
        statements, mentors and judges see them. Leave unset for an ordinary event.
      </p>

      {value && eventId && (
        <button
          type="button"
          onClick={linkExisting}
          disabled={linking}
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
        >
          {linking ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          {linking ? 'Linking…' : 'Bring existing registrations into the hackathon'}
        </button>
      )}
      {value && eventId && (
        <p className="mt-1 text-xs text-gray-500">
          Only needed once, for people who registered before the link was made.
        </p>
      )}

      {hackathons.length === 0 && (
        <p className="mt-2 text-sm text-amber-800">
          No hackathons exist yet. Create one under Hackathons first, then come back and link it.
        </p>
      )}
    </div>
  );
}
